import { useState, useEffect, useCallback } from 'react';
import {
  Page,
  Card,
  TextField,
  Button,
  BlockStack,
  InlineStack,
  Text,
  Select,
  Checkbox,
  Banner,
  Spinner,
  Divider,
  Badge
} from '@shopify/polaris';
import { trackEvent as trackAnalyticsEvent } from '../utils/analytics';

export default function Settings({ shop, shopData, aiReady, showToast, refreshShopData }) {
  const [settings, setSettings] = useState({
    default_transaction_fee_percent: 3.0,
    default_shipping_rate: 0,
    alert_margin_threshold: 20,
    discount_margin_buffer_percent: 10,
    shipping_cost_alert_percent: 12,
    currency: 'USD',
    ai_enabled: true,
    profit_target_percent: 25
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [billingStatus, setBillingStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [lastSyncAt, setLastSyncAt] = useState(shopData?.last_sync_at || null);

  const fetchSettings = useCallback(async () => {
    if (!shop) return;

    setLoading(true);
    try {
      const [settingsResponse, billingResponse, shopResponse] = await Promise.all([
        fetch(`/api/settings?shop=${shop}`),
        fetch(`/api/billing/status?shop=${shop}`).catch(() => null),
        fetch(`/api/shop?shop=${shop}`).catch(() => null)
      ]);

      if (settingsResponse.ok) {
        const data = await settingsResponse.json();
        setSettings(prev => ({ ...prev, ...data }));
      }

      if (billingResponse && billingResponse.ok) {
        const billingData = await billingResponse.json();
        setBillingStatus(billingData);
      }

      if (shopResponse && shopResponse.ok) {
        const shopSummary = await shopResponse.json();
        setLastSyncAt(shopSummary?.last_sync_at || null);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      showToast('Failed to load settings', true);
    } finally {
      setLoading(false);
    }
  }, [shop, showToast]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  useEffect(() => {
    setLastSyncAt(shopData?.last_sync_at || null);
  }, [shopData?.last_sync_at]);

  const handleSettingChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!shop) {
      showToast('Cannot save settings: shop parameter missing', true);
      return;
    }

    setSaving(true);
    try {
      const response = await fetch('/api/settings', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          shop,
          settings
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.message || errorData.error || 'Failed to save settings';
        throw new Error(errorMessage);
      }

      const result = await response.json();
      
      if (result.success) {
        showToast('Settings saved successfully');
        setHasChanges(false);
      } else {
        throw new Error('Save failed: no success confirmation');
      }

    } catch (error) {
      console.error('Error saving settings:', error);
      showToast(error.message || 'Failed to save settings', true);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    fetchSettings();
    setHasChanges(false);
  };

  const handleExportOrders = () => {
    if (!shop) return;
    window.open(`/api/export?shop=${shop}&type=orders`, '_blank');
  };

  const handleExportProducts = () => {
    if (!shop) return;
    window.open(`/api/export?shop=${shop}&type=products`, '_blank');
  };

  const handleFounderReport = () => {
    if (!shop) return;
    trackAnalyticsEvent('founder_report_exported', {}, { shop });
    window.open(`/api/export?shop=${shop}&type=founder_report`, '_blank');
  };

  const handleSyncData = async () => {
    setSyncing(true);
    try {
      const response = await fetch('/api/sync', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ shop })
      });

      if (response.ok) {
        const result = await response.json();
        setLastSyncAt(result.last_sync_at || new Date().toISOString());
        await refreshShopData?.();
        showToast(result.message || `Sync complete! ${result.products_synced} products, ${result.orders_synced} orders synced.`);
      } else {
        const error = await response.json();
        showToast(error.error || 'Sync failed', true);
      }
    } catch (error) {
      showToast('Sync failed - check console for details', true);
      console.error('Sync error:', error);
    } finally {
      setSyncing(false);
    }
  };

  // Track paywall view once per session when non-pro user sees billing section
  useEffect(() => {
    if (loading) return;
    if (shopData?.subscription_tier === 'pro') return;
    if (sessionStorage.getItem('profitpulse_paywall_viewed_tracked')) return;
    trackAnalyticsEvent('paywall_viewed', { current_tier: shopData?.subscription_tier || 'free' }, { shop });
    sessionStorage.setItem('profitpulse_paywall_viewed_tracked', '1');
  }, [loading, shop, shopData?.subscription_tier]);

  if (loading) {
    return (
      <Page title="Settings">
        <Card>
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <Spinner size="large" />
          </div>
        </Card>
      </Page>
    );
  }

  const PROFIT_TARGETS = [
    { label: '15% - Getting started', value: '15' },
    { label: '25% - Healthy business', value: '25' },
    { label: '35% - Strong growth', value: '35' },
    { label: '50% - Premium brand', value: '50' }
  ];

  return (
    <Page
      title="Settings"
      subtitle="Tune margin protection rules, profit goals, and privacy controls"
      primaryAction={
        hasChanges ? {
          content: 'Save Changes',
          loading: saving,
          onAction: handleSave,
        } : undefined
      }
      secondaryActions={
        hasChanges ? [
          {
            content: 'Reset',
            onAction: handleReset,
          },
        ] : []
      }
    >
      <BlockStack gap="400">
        {/* Profit Target */}
        <Card>
          <BlockStack gap="400">
            <Text variant="headingMd" as="h2">Profit Goal Tracking</Text>

            <Select
              label="Target Net Margin"
              options={PROFIT_TARGETS}
              value={String(settings.profit_target_percent || 25)}
              onChange={(value) => handleSettingChange('profit_target_percent', parseInt(value))}
              helpText="Profit Pulse AI compares your true net margin against this goal and highlights the leak patterns keeping you below it"
            />
          </BlockStack>
        </Card>

        {/* Cost Calculation Settings */}
        <Card>
          <BlockStack gap="400">
            <Text variant="headingMd" as="h2">Profit Inputs</Text>

            <TextField
              label="Default Transaction Fee (%)"
              type="number"
              value={settings.default_transaction_fee_percent?.toString()}
              onChange={(value) => handleSettingChange('default_transaction_fee_percent', parseFloat(value))}
              suffix="%"
              autoComplete="off"
              step="0.1"
              min="0"
              max="100"
              helpText="Used in true profit calculations when order-level fee data is not provided"
            />

            <TextField
              label="Default Shipping Rate"
              type="number"
              value={settings.default_shipping_rate?.toString()}
              onChange={(value) => handleSettingChange('default_shipping_rate', parseFloat(value))}
              prefix="$"
              autoComplete="off"
              step="0.01"
              min="0"
              helpText="Fallback shipping cost when Shopify order data does not include a reliable value"
            />

            <Select
              label="Currency"
              options={[
                { label: 'USD ($)', value: 'USD' },
                { label: 'EUR', value: 'EUR' },
                { label: 'GBP', value: 'GBP' },
                { label: 'CAD ($)', value: 'CAD' },
                { label: 'AUD ($)', value: 'AUD' },
              ]}
              value={settings.currency}
              onChange={(value) => handleSettingChange('currency', value)}
              helpText="Primary currency for your store"
            />
          </BlockStack>
        </Card>

        {/* Alert Settings */}
        <Card>
          <BlockStack gap="400">
            <Text variant="headingMd" as="h2">Margin Protection Rules</Text>

            <TextField
              label="Product Margin Threshold (%)"
              type="number"
              value={settings.alert_margin_threshold?.toString()}
              onChange={(value) => handleSettingChange('alert_margin_threshold', parseFloat(value))}
              suffix="%"
              autoComplete="off"
              step="1"
              min="0"
              max="100"
              helpText="Flag products that are running below this margin so you can reprice them before more low-profit orders come in"
            />

            <TextField
              label="Discount Margin Buffer (%)"
              type="number"
              value={settings.discount_margin_buffer_percent?.toString()}
              onChange={(value) => handleSettingChange('discount_margin_buffer_percent', parseFloat(value))}
              suffix="%"
              autoComplete="off"
              step="1"
              min="0"
              max="100"
              helpText="Alert when discounts consume more than this share of order revenue"
            />

            <TextField
              label="High Shipping Threshold (%)"
              type="number"
              value={settings.shipping_cost_alert_percent?.toString()}
              onChange={(value) => handleSettingChange('shipping_cost_alert_percent', parseFloat(value))}
              suffix="%"
              autoComplete="off"
              step="1"
              min="0"
              max="100"
              helpText="Alert when shipping cost rises above this share of order revenue"
            />

            <Banner tone="info">
              <p>Negative-margin orders always trigger an alert whenever true order profit falls below $0.</p>
            </Banner>
          </BlockStack>
        </Card>

        {/* AI Settings */}
        <Card>
          <BlockStack gap="400">
            <Text variant="headingMd" as="h2">Browser AI</Text>

            <Checkbox
              label="Enable AI-powered recommendations"
              checked={settings.ai_enabled}
              onChange={(value) => handleSettingChange('ai_enabled', value)}
              helpText="Runs in the browser when possible to summarize leak patterns and next-best actions"
            />

            {aiReady ? (
              <Banner tone="success">
                <p>Browser AI is loaded and ready for privacy-forward recommendations.</p>
              </Banner>
            ) : (
              <Banner tone="info">
                <p>The on-device AI model is loading in the background...</p>
              </Banner>
            )}
          </BlockStack>
        </Card>

        <Card>
          <BlockStack gap="400">
            <Text variant="headingMd" as="h2">Data Access & Privacy</Text>

            <Text as="p" tone="subdued">
              Profit Pulse AI is designed to run recommendation summaries in your browser where possible and avoids sending store data to external AI APIs by default.
            </Text>

            <BlockStack gap="200">
              <Text variant="headingSm" as="h3">What the app accesses</Text>
              <Text as="p" tone="subdued">
                Shopify order totals, shipping costs, fees, discounts, dates, product titles, prices, and the product costs you enter.
              </Text>
            </BlockStack>

            <BlockStack gap="200">
              <Text variant="headingSm" as="h3">What stays private</Text>
              <Text as="p" tone="subdued">
                AI-generated recommendations are produced in-browser when supported, and customer PII is not needed for the dashboard experience.
              </Text>
            </BlockStack>

            <Banner tone="success">
              <p>Need a full record? The privacy policy explains exactly what data is accessed, stored, and retained.</p>
            </Banner>
          </BlockStack>
        </Card>

        {/* Subscription & Billing */}
        <Card>
          <BlockStack gap="400">
            <Text variant="headingMd" as="h2">Subscription & Billing</Text>

            <InlineStack gap="600" wrap={true}>
              <BlockStack gap="100">
                <Text variant="bodySm" tone="subdued">Current Plan</Text>
                <InlineStack gap="200" blockAlign="center">
                  <Text variant="headingMd" as="p">
                    {shopData?.subscription_tier === 'pro' ? 'Pro' :
                     shopData?.subscription_tier === 'trial' ? 'Pro (Trial)' :
                     'Free'}
                  </Text>
                  <Badge tone={shopData?.subscription_status === 'active' && shopData?.subscription_tier === 'pro' ? 'success' : 'info'}>
                    {shopData?.subscription_tier === 'pro' ? 'Active' :
                     shopData?.subscription_tier === 'trial' ? 'Trial' : 'Free'}
                  </Badge>
                </InlineStack>
              </BlockStack>

              <BlockStack gap="100">
                <Text variant="bodySm" tone="subdued">Orders This Period</Text>
                <Text variant="headingMd" as="p">
                  {shopData?.current_period_orders || 0}
                </Text>
              </BlockStack>

              {billingStatus?.current_billing && (
                <BlockStack gap="100">
                  <Text variant="bodySm" tone="subdued">Current Charge</Text>
                  <Text variant="headingMd" as="p">
                    ${billingStatus.current_billing.total_amount}
                  </Text>
                </BlockStack>
              )}
            </InlineStack>

            <Divider />

            {shopData?.subscription_tier === 'pro' ? (
              <InlineStack gap="200">
                <Badge tone="success">Pro plan active</Badge>
                <Text variant="bodySm" as="span" tone="subdued">
                  Manage billing through Shopify Admin
                </Text>
              </InlineStack>
            ) : (
              <BlockStack gap="400">
                {/* Free vs Pro comparison */}
                <InlineStack gap="400" wrap={true}>
                  {/* Free tier */}
                  <div style={{ flex: 1, minWidth: '240px' }}>
                    <Card>
                      <BlockStack gap="300">
                        <Text variant="headingMd" as="h3">Free</Text>
                        <Text variant="heading2xl" as="p">$0</Text>
                        <Text variant="bodySm" as="p" tone="subdued">Current plan · no credit card required</Text>
                        <Divider />
                        <BlockStack gap="200">
                          <Text variant="bodySm" as="p">Last 30 days of orders</Text>
                          <Text variant="bodySm" as="p">True profit per order</Text>
                          <Text variant="bodySm" as="p">On-device AI insights</Text>
                          <Text variant="bodySm" as="p" tone="subdued">No Action Items</Text>
                          <Text variant="bodySm" as="p" tone="subdued">No goal tracking</Text>
                        </BlockStack>
                      </BlockStack>
                    </Card>
                  </div>

                  {/* Pro tier */}
                  <div style={{ flex: 1, minWidth: '240px' }}>
                    <Card background="bg-surface-success">
                      <BlockStack gap="300">
                        <InlineStack gap="200" blockAlign="center">
                          <Text variant="headingMd" as="h3">Pro</Text>
                          <Badge tone="success">Recommended</Badge>
                        </InlineStack>
                        <InlineStack gap="100" blockAlign="end">
                          <Text variant="heading2xl" as="p">$19</Text>
                          <Text variant="bodySm" as="p" tone="subdued">/month</Text>
                        </InlineStack>
                        <Text variant="bodySm" as="p" tone="subdued">Billed monthly · cancel anytime</Text>
                        <Divider />
                        <BlockStack gap="200">
                          <Text variant="bodySm" as="p">90-day order history</Text>
                          <Text variant="bodySm" as="p">Recommendations to increase profit</Text>
                          <Text variant="bodySm" as="p">Profit goal tracking</Text>
                          <Text variant="bodySm" as="p">Negative-margin and low-margin alerts</Text>
                          <Text variant="bodySm" as="p">CSV data export</Text>
                          <Text variant="bodySm" as="p">Priority support</Text>
                        </BlockStack>
                        <Button
                          variant="primary"
                          fullWidth
                          onClick={async () => {
                            trackAnalyticsEvent('billing_subscribe_clicked', { plan: 'pro' }, { shop });
                            try {
                              const response = await fetch('/api/billing/subscribe', {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ shop, plan: 'pro' })
                              });
                              if (response.ok) {
                                const data = await response.json();
                                if (data.confirmation_url) {
                                  trackAnalyticsEvent('subscription_started', { plan: 'pro', price: 19 }, { shop });
                                  window.open(data.confirmation_url, '_top');
                                }
                              } else {
                                showToast('Failed to start upgrade', true);
                              }
                            } catch (err) {
                              showToast('Failed to start upgrade', true);
                            }
                          }}
                        >
                            Start 7-day free trial
                        </Button>
                        <Text variant="bodySm" as="p" tone="subdued" alignment="center">
                          No charge for 7 days · cancel anytime in Shopify
                        </Text>
                      </BlockStack>
                    </Card>
                  </div>
                </InlineStack>

                <Banner tone="info">
                  <p>
                    Looking at a longer history makes profit leaks easier to spot.
                    Merchants who review a full quarter often uncover margin drag from discounts and shipping they would have missed in a weekly view.
                  </p>
                </Banner>
              </BlockStack>
            )}
          </BlockStack>
        </Card>

        {/* Data Export & Sync */}
        <Card>
          <BlockStack gap="400">
            <Text variant="headingMd" as="h2">Data Management</Text>

            <Text as="p" tone="subdued">
              Sync fresh Shopify data or export the exact order and product inputs behind your margin analysis.
            </Text>

            <InlineStack gap="200" wrap>
              <Button
                onClick={handleSyncData}
                loading={syncing}
                disabled={syncing}
              >
                {syncing ? 'Syncing...' : 'Sync from Shopify'}
              </Button>
              <Button variant="primary" onClick={handleFounderReport}>
                Download Profit Leak Report
              </Button>
              <Button onClick={handleExportOrders}>
                Export Orders (CSV)
              </Button>
              <Button onClick={handleExportProducts}>
                Export Products (CSV)
              </Button>
            </InlineStack>
            <Text variant="bodySm" as="p" tone="subdued">
              The Profit Leak Report includes a 90-day summary of negative-margin orders, discount leakage, shipping drag, low-margin products, and COGS coverage score in a founder-ready format.
            </Text>

            <Text as="p" variant="bodySm" tone="subdued">
              Last synced: {lastSyncAt
                ? new Date(lastSyncAt).toLocaleString()
                : 'Never'}
            </Text>
          </BlockStack>
        </Card>

        {/* Danger Zone */}
        <Card>
          <BlockStack gap="400">
            <Text variant="headingMd" as="h2" tone="critical">Danger Zone</Text>

            <Text as="p" tone="subdued">
              Uninstalling the app will stop order analysis and webhook processing.
              Your data will be retained for 30 days.
            </Text>

            <InlineStack gap="200">
              <Button
                tone="critical"
                onClick={() => showToast('To uninstall, remove the app from your Shopify admin')}
              >
                Uninstall App
              </Button>
            </InlineStack>
          </BlockStack>
        </Card>
      </BlockStack>
    </Page>
  );
}
